import React, { useEffect, useState } from 'react';
import { MultiSelect } from '../ui/MultiSelect';
import { Input } from '../ui/Input';
import { AlertCircle } from 'lucide-react';
export function KYCTab() {
  const [selectedKYCGroups, setSelectedKYCGroups] = useState<string[]>([]);
  const [freshDays, setFreshDays] = useState('90');
  const [staleFrom, setStaleFrom] = useState('91');
  const [staleTo, setStaleTo] = useState('180');
  const [expiredFrom, setExpiredFrom] = useState('181');
  // Auto-calculate stale and expired ranges based on fresh days
  useEffect(() => {
    const fresh = parseInt(freshDays) || 0;
    if (fresh > 0) {
      setStaleFrom((fresh + 1).toString());
    }
  }, [freshDays]);
  // Auto-calculate expired from based on stale to
  useEffect(() => {
    const staleLast = parseInt(staleTo) || 0;
    if (staleLast > 0) {
      setExpiredFrom((staleLast + 1).toString());
    }
  }, [staleTo]);
  // Validate stale range
  const validateStaleRange = () => {
    const fresh = parseInt(freshDays) || 0;
    const staleStart = parseInt(staleFrom) || 0;
    const staleEnd = parseInt(staleTo) || 0;
    if (staleStart <= fresh) {
      return false;
    }
    if (staleEnd <= staleStart) {
      return false;
    }
    return true;
  };
  const isValidRange = validateStaleRange();
  return <div className="space-y-6 max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-704">
      <MultiSelect label="KYC Groups (Optional)" value={selectedKYCGroups} onChange={setSelectedKYCGroups} placeholder="Select one or multiple KYC groups..." options={[{
      value: 'basic',
      label: 'Basic KYC'
    }, {
      value: 'standard',
      label: 'Standard KYC'
    }, {
      value: 'enhanced',
      label: 'Enhanced Due Diligence'
    }, {
      value: 'corporate',
      label: 'Corporate KYC'
    }, {
      value: 'high_net_worth',
      label: 'High Net Worth KYC'
    }]} data-id="element-705" />

      {/* KYC Validity Period Configuration */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-706">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-707">
          KYC Validity Period Configuration
        </h3>
        <p className="text-sm text-gray-600 mb-6" data-id="element-708">
          Define the validity periods for KYC documents. Ranges are
          automatically calculated to prevent overlaps.
        </p>

        <div className="space-y-6" data-id="element-709">
          {/* Fresh KYC */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200" data-id="element-710">
            <div className="flex items-center justify-between mb-3" data-id="element-711">
              <div className="flex items-center space-x-2" data-id="element-712">
                <div className="h-3 w-3 bg-green-500 rounded-full" data-id="element-713"></div>
                <label className="text-sm font-semibold text-green-900" data-id="element-714">
                  Fresh KYC
                </label>
              </div>
              <span className="text-xs text-green-700 font-medium" data-id="element-715">
                Most Recent
              </span>
            </div>
            <div className="flex items-center space-x-3" data-id="element-716">
              <span className="text-sm text-green-700 font-medium" data-id="element-717">≤</span>
              <div className="w-32" data-id="element-718">
                <Input type="number" value={freshDays} onChange={e => setFreshDays(e.target.value)} min="1" placeholder="90" data-id="element-719" />
              </div>
              <span className="text-sm text-green-700 font-medium" data-id="element-720">days</span>
            </div>
            <p className="text-xs text-green-600 mt-2" data-id="element-721">
              KYC documents updated within the last {freshDays || '0'} days
            </p>
          </div>

          {/* Stale KYC */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200" data-id="element-722">
            <div className="flex items-center justify-between mb-3" data-id="element-723">
              <div className="flex items-center space-x-2" data-id="element-724">
                <div className="h-3 w-3 bg-yellow-500 rounded-full" data-id="element-725"></div>
                <label className="text-sm font-semibold text-yellow-900" data-id="element-726">
                  Stale KYC
                </label>
              </div>
              <span className="text-xs text-yellow-700 font-medium" data-id="element-727">
                Needs Review
              </span>
            </div>
            <div className="flex items-center space-x-3" data-id="element-728">
              <div className="w-32" data-id="element-729">
                <Input type="number" value={staleFrom} disabled className="bg-gray-100" data-id="element-730" />
              </div>
              <span className="text-sm text-yellow-700 font-medium" data-id="element-731">to</span>
              <div className="w-32" data-id="element-732">
                <Input type="number" value={staleTo} onChange={e => setStaleTo(e.target.value)} min={parseInt(staleFrom) + 1} placeholder="180" data-id="element-733" />
              </div>
              <span className="text-sm text-yellow-700 font-medium" data-id="element-734">days</span>
            </div>
            <p className="text-xs text-yellow-600 mt-2" data-id="element-735">
              KYC documents between {staleFrom} and {staleTo} days old
              (auto-calculated from Fresh period)
            </p>
            {!isValidRange && staleTo && <div className="flex items-center space-x-2 mt-2 text-xs text-red-600" data-id="element-736">
                <AlertCircle className="h-3 w-3" data-id="element-737" />
                <span data-id="element-738">Stale end must be greater than stale start</span>
              </div>}
          </div>

          {/* Expired KYC */}
          <div className="bg-red-50 p-4 rounded-lg border border-red-200" data-id="element-739">
            <div className="flex items-center justify-between mb-3" data-id="element-740">
              <div className="flex items-center space-x-2" data-id="element-741">
                <div className="h-3 w-3 bg-red-500 rounded-full" data-id="element-742"></div>
                <label className="text-sm font-semibold text-red-900" data-id="element-743">
                  Expired KYC
                </label>
              </div>
              <span className="text-xs text-red-700 font-medium" data-id="element-744">
                Requires Update
              </span>
            </div>
            <div className="flex items-center space-x-3" data-id="element-745">
              <span className="text-sm text-red-700 font-medium" data-id="element-746">&gt;</span>
              <div className="w-32" data-id="element-747">
                <Input type="number" value={expiredFrom} disabled className="bg-gray-100" data-id="element-748" />
              </div>
              <span className="text-sm text-red-700 font-medium" data-id="element-749">days</span>
            </div>
            <p className="text-xs text-red-600 mt-2" data-id="element-750">
              KYC documents older than {expiredFrom} days (auto-calculated from
              Stale period)
            </p>
          </div>
        </div>

        {/* Visual Timeline */}
        <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-751">
          <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-752">
            Validity Timeline
          </h4>
          <div className="relative h-12 bg-gray-100 rounded-lg overflow-hidden" data-id="element-753">
            <div className="absolute h-full bg-green-400 flex items-center justify-center text-xs font-medium text-green-900" style={{
            width: `${parseInt(freshDays) / (parseInt(expiredFrom) || 1) * 100}%`,
            left: 0
          }} data-id="element-754">
              Fresh
            </div>
            <div className="absolute h-full bg-yellow-400 flex items-center justify-center text-xs font-medium text-yellow-900" style={{
            width: `${(parseInt(staleTo) - parseInt(staleFrom) + 1) / (parseInt(expiredFrom) || 1) * 100}%`,
            left: `${parseInt(freshDays) / (parseInt(expiredFrom) || 1) * 100}%`
          }} data-id="element-755">
              Stale
            </div>
            <div className="absolute h-full bg-red-400 flex items-center justify-center text-xs font-medium text-red-900" style={{
            width: `${100 - parseInt(staleTo) / (parseInt(expiredFrom) || 1) * 100}%`,
            right: 0
          }} data-id="element-756">
              Expired
            </div>
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2" data-id="element-757">
            <span data-id="element-758">0 days</span>
            <span data-id="element-759">{freshDays} days</span>
            <span data-id="element-760">{staleTo} days</span>
            <span data-id="element-761">{expiredFrom}+ days</span>
          </div>
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 p-4 rounded-md border border-blue-100" data-id="element-762">
        <h4 className="text-sm font-semibold text-blue-800 mb-2" data-id="element-763">
          KYC Configuration Guidelines
        </h4>
        <ul className="text-sm text-blue-600 space-y-1" data-id="element-764">
          <li data-id="element-765">
            • <strong data-id="element-766">Fresh KYC:</strong> Documents are current and valid for
            immediate processing
          </li>
          <li data-id="element-767">
            • <strong data-id="element-768">Stale KYC:</strong> Documents may need review or update
            before approval
          </li>
          <li data-id="element-769">
            • <strong data-id="element-770">Expired KYC:</strong> Documents must be updated before
            processing
          </li>
          <li data-id="element-771">• Ranges are automatically calculated to prevent overlaps</li>
          <li data-id="element-772">• Select KYC groups required for this loan product above</li>
        </ul>
      </div>

      {/* Validation Summary */}
      {isValidRange ? <div className="bg-green-50 p-4 rounded-md border border-green-200 flex items-start space-x-3" data-id="element-773">
          <div className="h-5 w-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-774">
            <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20" data-id="element-775">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" data-id="element-776" />
            </svg>
          </div>
          <div data-id="element-777">
            <p className="text-sm font-medium text-green-900" data-id="element-778">
              Valid Configuration
            </p>
            <p className="text-xs text-green-700 mt-1" data-id="element-779">
              All validity periods are properly configured with no overlaps
            </p>
          </div>
        </div> : <div className="bg-red-50 p-4 rounded-md border border-red-200 flex items-start space-x-3" data-id="element-780">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-781" />
          <div data-id="element-782">
            <p className="text-sm font-medium text-red-900" data-id="element-783">
              Invalid Configuration
            </p>
            <p className="text-xs text-red-700 mt-1" data-id="element-784">
              Please ensure stale end date is greater than stale start date
            </p>
          </div>
        </div>}
    </div>;
}